import { useState, useRef, useEffect } from "react";
import { Table, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

function GenericScrollTable({ 
  data, 
  columns, 
  title, 
  icon = "bi-table",
  initialItems = 10,
  incrementItems = 10,
  height = "400px",
  onRowClick = null,
  renderCell = null,
  emptyMessage = "No hay datos disponibles"
}) {
  const [cantidadMostrada, setCantidadMostrada] = useState(initialItems);
  const [cargandoMas, setCargandoMas] = useState(false);
  const tableContainerRef = useRef(null);
  const navigate = useNavigate();

  // Resetear cantidad mostrada cuando cambien los datos
  useEffect(() => {
    setCantidadMostrada(initialItems);
  }, [data, initialItems]);

  // Manejar scroll infinito
  const handleScroll = (e) => {
    const element = e.target;
    const scrollPosition = element.scrollTop + element.clientHeight;
    const scrollHeight = element.scrollHeight;

    // Si llegamos al 90% del scroll y hay más datos para mostrar
    if (scrollPosition >= scrollHeight * 0.9 && cantidadMostrada < (data?.length || 0) && !cargandoMas) {
      setCargandoMas(true);
      
      // Simular pequeño delay para mejor UX
      setTimeout(() => {
        setCantidadMostrada(prev => Math.min(prev + incrementItems, data?.length || 0));
        setCargandoMas(false);
      }, 300);
    }
  };

  const datosAMostrar = data?.slice(0, cantidadMostrada) || [];

  // Manejar click en fila
  const handleRowClick = (row, index) => {
    if (onRowClick) {
      onRowClick(row, index, navigate);
    }
  };

  // Estilos para la barra de scroll personalizada
  const scrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 12px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 10px;
    }
    
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
    
    .table-scroll-container {
      display: block;
      width: 100%;
      overflow-x: auto;
      overflow-y: scroll;
    }
    
    .table-scroll-container table {
      width: 100%;
      margin-bottom: 0;
    }
    
    .table-scroll-container thead {
      position: sticky;
      top: 0;
      z-index: 10;
      background: #f8f9fa;
    }
    
    .clickable-row {
      cursor: pointer;
      transition: background-color 0.2s ease;
    }
    
    .clickable-row:hover {
      background-color: rgba(13, 110, 253, 0.1) !important;
    }
  `;

  // Renderizar celda
  const renderCellContent = (row, column, index) => {
    if (renderCell) {
      return renderCell(row, column, index);
    }
    
    // Si la columna tiene un render personalizado
    if (column.render) {
      return column.render(row, index);
    }
    
    // Renderizado por defecto
    return row[column.key];
  };

  return (
    <div className="card shadow-sm">
      <style>{scrollbarStyles}</style>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">
            <i className={`bi ${icon} me-2`}></i>
            {title}
          </h5>
          {data && data.length > cantidadMostrada && (
            <small className="text-muted">
              Mostrando {cantidadMostrada} de {data.length} • Desplázate para ver más
            </small>
          )}
        </div>

        {data && data.length > 0 ? (
          <div 
            ref={tableContainerRef}
            onScroll={handleScroll}
            className="table-scroll-container custom-scrollbar" 
            style={{ 
              maxHeight: height,
              height: height,
              scrollbarWidth: 'thin',
              scrollbarColor: '#888 #f1f1f1'
            }}
          >
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  {columns.map((column, index) => (
                    <th 
                      key={index}
                      className={column.className || ''}
                      style={column.style || {}}
                    >
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datosAMostrar.map((row, index) => (
                  <tr 
                    key={row.id || index}
                    onClick={() => handleRowClick(row, index)}
                    className={onRowClick ? "clickable-row" : ""}
                  >
                    {columns.map((column, colIndex) => (
                      <td 
                        key={colIndex}
                        className={column.cellClassName || ''}
                        style={column.cellStyle || {}}
                      >
                        {renderCellContent(row, column, index)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {/* Espaciador para permitir scroll cuando hay pocos elementos */}
            {data && data.length > cantidadMostrada && (
              <div style={{ height: '100px' }}></div>
            )}
            
            {/* Indicador de carga al final de la tabla */}
            {cargandoMas && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" variant="primary" />
                <small className="text-muted ms-2">Cargando más datos...</small>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-inbox" style={{ fontSize: "3rem" }}></i>
            <p className="mt-3">{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
}

GenericScrollTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      header: PropTypes.string.isRequired,
      className: PropTypes.string,
      style: PropTypes.object,
      cellClassName: PropTypes.string,
      cellStyle: PropTypes.object,
      render: PropTypes.func,
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  initialItems: PropTypes.number,
  incrementItems: PropTypes.number,
  height: PropTypes.string,
  onRowClick: PropTypes.func,
  renderCell: PropTypes.func,
  emptyMessage: PropTypes.string,
};

export default GenericScrollTable;
